import React, { useEffect, useId, useRef, useState } from 'react';

/**
 * A button that opens a listbox of statuses. Unlike a native <select>, whose
 * change event fires on every arrow key, a choice is committed only by Enter,
 * Space or a click; arrows, Home and End only move the highlight, and Escape
 * or Tab closes without a change. Clicks and keys stay inside the menu so the
 * card underneath neither opens nor drags.
 */
export function StatusMenu({ label, value, options, onChoose, focusOnMount = false }: {
    label: string;
    value: string;
    options: { value: string; label: string }[];
    onChoose: (value: string) => void;
    /** Focus the trigger when mounted (a card re-mounts in its new column after a move). */
    focusOnMount?: boolean;
}) {
    const [open, setOpen] = useState(false);
    const [active, setActive] = useState(0);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const listRef = useRef<HTMLUListElement>(null);
    const id = useId();
    const current = options.find(o => o.value === value);

    useEffect(() => { if (focusOnMount) triggerRef.current?.focus(); }, [focusOnMount]);
    useEffect(() => { if (open) listRef.current?.focus(); }, [open]);

    function show() {
        setActive(Math.max(0, options.findIndex(o => o.value === value)));
        setOpen(true);
    }
    function close(refocus: boolean) {
        setOpen(false);
        if (refocus) triggerRef.current?.focus();
    }
    function choose(i: number) {
        close(true);
        if (options[i].value !== value) onChoose(options[i].value);
    }
    function onKeyDown(e: React.KeyboardEvent) {
        e.stopPropagation();
        const n = options.length;
        const move: Record<string, number> = { ArrowDown: (active + 1) % n, ArrowUp: (active - 1 + n) % n, Home: 0, End: n - 1 };
        if (e.key in move) { e.preventDefault(); setActive(move[e.key]); }
        else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); choose(active); }
        else if (e.key === 'Escape') { e.preventDefault(); close(true); }
        else if (e.key === 'Tab') close(false);
    }

    return (
        <div className="status-menu" onClick={e => e.stopPropagation()} onKeyDown={e => e.stopPropagation()}
            onMouseDown={e => e.stopPropagation()} draggable={false} onDragStart={e => { e.preventDefault(); e.stopPropagation(); }}>
            <button ref={triggerRef} type="button" className="form-input status-menu-trigger"
                aria-label={`${label}: ${current?.label ?? value}`} aria-haspopup="listbox" aria-expanded={open}
                onClick={() => (open ? close(true) : show())}>
                {current?.label ?? value}
            </button>
            {open && (
                <ul ref={listRef} role="listbox" aria-label={label} tabIndex={-1} className="status-menu-list"
                    aria-activedescendant={`${id}-${active}`} onKeyDown={onKeyDown} onBlur={() => close(false)}>
                    {options.map((o, i) => (
                        <li key={o.value} id={`${id}-${i}`} role="option" aria-selected={o.value === value}
                            className={`status-menu-option${i === active ? ' is-active' : ''}`}
                            onMouseDown={e => e.preventDefault() /* keep focus in the list */}
                            onMouseEnter={() => setActive(i)}
                            onClick={() => choose(i)}>
                            {o.label}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
